/**
 * Persistent context banner showing which submission is in focus (A2F-457).
 *
 * Renders on filtered pages when a submission is selected in the sidebar.
 * Route-based: checks the current path against FILTERED_ROUTES to decide
 * whether to show. Reads from SubmissionFocusContext.
 */

import { useLocation } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useSubmissionFocus } from '@/lib/SubmissionFocusContext';

/** Routes where the submission focus filters the displayed data.
 *
 * Documents pages (Signoff, Clearance) deliberately use their own page-local
 * "Active subs" pills filtered to `isPlanning` submissions, so they're not
 * in this list — the global banner would otherwise double up with the local
 * pill row.
 */
const FILTERED_ROUTES = [
  // A2F-1262: `/` (the old Dashboard) is gone — it redirects to /review/summary,
  // which renders its OWN inline focus banner, so it stays out of this global
  // allowlist (enrolling it would double up, like the Documents pages).
  '/review/abbreviations',
  '/review/cross-references',
  '/review/dose-unit',
  '/review/rename-consistency',
  // A2F: Study Profile now honors the submission focus on read + scan, so it
  // gets the same persistent focus banner as the other Review surfaces.
  '/review/study-profile',
];

export function SubmissionFocusBanner() {
  const { submissionFocusId, focusedSubmission } = useSubmissionFocus();
  const { pathname } = useLocation();

  if (!submissionFocusId) return null;
  if (!FILTERED_ROUTES.includes(pathname)) return null;

  const label = focusedSubmission
    ? `Submission ${focusedSubmission.displaySequence}${focusedSubmission.submissionType ? ' · ' + focusedSubmission.submissionType : ''}`
    : `Submission`;

  return (
    <div className="mb-4 px-3 py-2 bg-orange-50 border border-orange-100 rounded-lg flex items-center gap-2 text-sm text-brand-charcoal">
      <Search className="w-3.5 h-3.5 text-brand-orange shrink-0" />
      <span>Showing data for <strong>{label}</strong></span>
    </div>
  );
}
